import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const content = {
    privacy: {
      title: 'Política de Privacidad',
      body: <div className="space-y-4 text-cream-muted font-sans text-sm leading-relaxed" data-id="element-33">
          <p data-id="element-34">
            <strong className="text-cream" data-id="element-35">Última actualización:</strong> Abril
            2026
          </p>
          <p data-id="element-36">
            En El Tesoro de Dubái, nos comprometemos a proteger tu privacidad.
            Esta política describe cómo recopilamos, usamos y protegemos tu
            información personal.
          </p>
          <h3 className="text-cream font-semibold text-base mt-6" data-id="element-37">
            1. Información que recopilamos
          </h3>
          <p data-id="element-38">
            Recopilamos información que nos proporcionas directamente al
            realizar una compra, como tu nombre, dirección de correo electrónico
            y datos de pago. Los datos de pago son procesados de forma segura
            por nuestro proveedor de pagos y no se almacenan en nuestros
            servidores.
          </p>
          <h3 className="text-cream font-semibold text-base mt-6" data-id="element-39">
            2. Uso de la información
          </h3>
          <p data-id="element-40">
            Utilizamos tu información para procesar tu compra, enviarte el
            producto digital adquirido y comunicarnos contigo sobre tu pedido.
            No compartimos tu información personal con terceros con fines de
            marketing.
          </p>
          <h3 className="text-cream font-semibold text-base mt-6" data-id="element-41">
            3. Cookies
          </h3>
          <p data-id="element-42">
            Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
            navegación y analizar el tráfico del sitio. Puedes desactivar las
            cookies en la configuración de tu navegador.
          </p>
          <h3 className="text-cream font-semibold text-base mt-6" data-id="element-43">
            4. Seguridad
          </h3>
          <p data-id="element-44">
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger tu información personal contra acceso no autorizado,
            alteración o destrucción.
          </p>
          <h3 className="text-cream font-semibold text-base mt-6" data-id="element-45">
            5. Tus derechos
          </h3>
          <p data-id="element-46">
            Tienes derecho a acceder, rectificar o eliminar tu información
            personal en cualquier momento. También puedes solicitar la
            portabilidad de tus datos o retirar tu consentimiento para el
            procesamiento de datos.
          </p>
        </div>
    },
    terms: {
      title: 'Términos y Condiciones',
      body: <div className="space-y-4 text-cream-muted font-sans text-sm leading-relaxed" data-id="element-47">
          <p data-id="element-48">
            <strong className="text-cream" data-id="element-49">Última actualización:</strong> Abril
            2026
          </p>
          <p data-id="element-50">
            Al acceder y utilizar este sitio web y adquirir nuestros productos
            digitales, aceptas estos términos y condiciones en su totalidad.
          </p>
          <h3 className="text-cream font-semibold text-base mt-6" data-id="element-51">
            1. Producto digital
          </h3>
          <p data-id="element-52">
            El Tesoro de Dubái en Casa es un producto digital (guía/receta) que
            se entrega de forma inmediata tras la compra. Al ser un producto
            digital de acceso instantáneo, no se aceptan devoluciones ni
            reembolsos una vez completada la descarga.
          </p>
          <h3 className="text-cream font-semibold text-base mt-6" data-id="element-53">
            2. Uso permitido
          </h3>
          <p data-id="element-54">
            La guía es para uso personal. Puedes utilizar las recetas para
            vender productos horneados, pero no puedes redistribuir, revender ni
            compartir el contenido digital de la guía en sí.
          </p>
          <h3 className="text-cream font-semibold text-base mt-6" data-id="element-55">
            3. Propiedad intelectual
          </h3>
          <p data-id="element-56">
            Todo el contenido de este sitio web y del producto digital,
            incluyendo textos, imágenes, diseños y métodos descritos, está
            protegido por derechos de autor y es propiedad exclusiva de El
            Tesoro de Dubái.
          </p>
          <h3 className="text-cream font-semibold text-base mt-6" data-id="element-57">
            4. Resultados
          </h3>
          <p data-id="element-58">
            Los resultados pueden variar según la experiencia del usuario, los
            ingredientes disponibles y las condiciones de preparación. No
            garantizamos resultados específicos.
          </p>
          <h3 className="text-cream font-semibold text-base mt-6" data-id="element-59">
            5. Limitación de responsabilidad
          </h3>
          <p data-id="element-60">
            No nos hacemos responsables de daños directos o indirectos derivados
            del uso de nuestros productos digitales o de la preparación de las
            recetas contenidas en ellos.
          </p>
        </div>
    }
  };
  const current = content[type];
  return <AnimatePresence data-id="element-61">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-62">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3
      }} className="bg-dark-lighter border border-white/10 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-63">
          <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-64">
            <h2 className="font-serif text-2xl text-gold" data-id="element-65">{current.title}</h2>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors text-cream-muted hover:text-cream" data-id="element-66">
              <X size={20} data-id="element-67" />
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-68">
            {current.body}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 px-6 bg-dark border-t border-white/5 text-center" data-id="element-69">
        <div className="max-w-4xl mx-auto" data-id="element-70">
          <p className="font-serif text-gold/70 text-lg mb-3" data-id="element-71">
            El Tesoro de Dubái
          </p>
          <div className="flex items-center justify-center gap-6 mb-4" data-id="element-72">
            <button onClick={() => setActiveModal('privacy')} className="font-sans text-sm text-cream-muted/60 hover:text-gold transition-colors underline underline-offset-2 decoration-white/10 hover:decoration-gold/50" data-id="element-73">
              Política de Privacidad
            </button>
            <span className="text-cream-muted/20" data-id="element-74">|</span>
            <button onClick={() => setActiveModal('terms')} className="font-sans text-sm text-cream-muted/60 hover:text-gold transition-colors underline underline-offset-2 decoration-white/10 hover:decoration-gold/50" data-id="element-75">
              Términos y Condiciones
            </button>
          </div>
          <p className="font-sans text-xs text-cream-muted/50" data-id="element-76">
            © {new Date().getFullYear()} El Tesoro de Dubái. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      <Modal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-77" />
    </>;
}